import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag, useDrop } from 'react-dnd-cjs';
import { getEmptyImage } from 'react-dnd-html5-backend-cjs';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { CardDropField, DraggableDataCard } from 'components';
import { dragItems } from 'constants/general';

const DraggableItem = (props) => {
	const {
		containerId,
		data,
		index,
		onDragEnd,
		onDragStart,
		onDrop,
		onForeignHover,
		onNativeHover,
		onRemove,
		onTextChange,
	} = props;

	const getItem = () => ({
		type: dragItems.ADDITIONAL_DATA,
		payload: data,
		containerId,
		index,
	});

	const cardWrapperRef = useRef({ offsetWidth: 0 });

	const [{ isDragging }, drag, preview] = useDrag({
		item: getItem(),
		end: onDragEnd,
		begin: (monitor) => {
			onDragStart(monitor);
			return {
				...getItem(),
				componentWidth: cardWrapperRef && cardWrapperRef.current ? cardWrapperRef.current.offsetWidth : 0,
			};
		},
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [, drop] = useDrop({
		accept: dragItems.ADDITIONAL_DATA,
		drop: (item) => {
			if (props.containerId === item.containerId) {
				onDrop(item.payload.id);
			}
		},
		hover(item) {
			if (props.containerId === item.containerId) {
				if (item.index === index) return;
				onNativeHover(item, index);
				item.index = index;
			} else {
				onForeignHover(index);
			}
		},
	});

	useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, [preview]);

	const handleChange = useCallback(
		(value) => {
			onTextChange(data.key, value);
		},
		[onTextChange, data],
	);

	const handleClose = useCallback(() => {
		onRemove(data.key);
	}, [onRemove, data]);

	return (
		<div ref={drop}>
			<div ref={drag} style={{ cursor: 'move' }}>
				{isDragging ? (
					<CardDropField animate dropFieldText={getLabelMessage('campaignState:dropText')} />
				) : (
					<div ref={cardWrapperRef}>
						<DraggableDataCard index={index} data={data} onChange={handleChange} onClose={handleClose} />
					</div>
				)}
			</div>
		</div>
	);
};

DraggableItem.propTypes = {
	containerId: PropTypes.string,
	data: PropTypes.object,
	index: PropTypes.number,
	isDragging: PropTypes.bool,
	onDragEnd: PropTypes.func,
	onDragStart: PropTypes.func,
	onDrop: PropTypes.func,
	onForeignHover: PropTypes.func,
	onNativeHover: PropTypes.func,
	onRemove: PropTypes.func,
	onTextChange: PropTypes.func,
};

export default DraggableItem;
